import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import InfiniteReferencesSlider from '../components/InfiniteReferencesSlider'
import ReferenceModal from '../components/ReferenceModal'

function getAvatarUrl(url) {
  if (!url) return null
  if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=160,height=160,fit=cover,quality=85,format=auto/')
  }
  return url
}

const MAX_CHAR_LENGTH = 200

function formatPreview(text, maxLen = MAX_CHAR_LENGTH) {
  if (!text) return { text: '', isTruncated: false }
  if (text.length <= maxLen) return { text, isTruncated: false }

  const sub = text.slice(0, maxLen)
  const lastSpace = sub.lastIndexOf(' ')
  const truncated = (lastSpace > 0 ? sub.slice(0, lastSpace) : sub).trim() + '...'
  return { text: truncated, isTruncated: true }
}

export default function ReferencesPage() {
  const [references, setReferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedModalIndex, setSelectedModalIndex] = useState(null)

  useEffect(() => {
    async function loadReferences() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/referencer')
        if (res.ok) {
          const data = await res.json()
          setReferences(data.items || [])
        }
      } catch (err) {
        console.error('Failed to load references', err)
      } finally {
        setLoading(false)
      }
    }
    loadReferences()
  }, [])

  return (
    <>
      <SEO
        title="Referencer & Anbefalinger"
        description="Læs udtalelser og anbefalinger fra CTO'er, HR-ansvarlige og IT-specialister om samarbejdet med Birgitte Vilsgaard."
      />

      {/* Header Banner */}
      <section className="bg-gradient-to-b from-slate-100 to-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-black text-black tracking-tight">
              Referencer
            </h1>
            <p className="text-lg text-black mt-3 leading-relaxed">
              Tilfredse kunder og kandidater er det bedste bevis på vores arbejde. Her finder du anbefalinger fra LinkedIn og direkte samarbejder.
            </p>
          </div>
        </div>
      </section>

      {/* Slider Section */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-black mt-1">
              Udtalelser & Anbefalinger
            </h2>
            <p className="text-black mt-2 text-sm max-w-2xl mx-auto">
              Læs hvad ledere, HR-ansvarlige og kandidater fortæller om Birgitte Vilsgaards rekrutteringsindsats.
            </p>
          </div>

          <InfiniteReferencesSlider />
        </div>
      </section>

      {/* Full Grid Section */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-black">
              Alle anbefalinger
            </h2>
            <p className="text-slate-700 mt-2 text-sm">
              Oversigt over udtalelser og erfaringer fra kandidater og virksomhedsledere.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {references.map((ref, idx) => {
                const avatar = Array.isArray(ref.billede) ? ref.billede[0] : ref.billede
                const { text: previewText, isTruncated } = formatPreview(ref.anbefaling, MAX_CHAR_LENGTH)

                return (
                  <div
                    key={ref.id || idx}
                    className="bg-slate-50 border border-slate-200 p-8 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-1 text-black mb-4">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="fa-solid fa-star text-xs text-black" aria-hidden="true" />
                        ))}
                      </div>
                      <p className="text-black text-sm italic leading-relaxed whitespace-pre-line">
                        "{previewText}"
                      </p>

                      {isTruncated && (
                        <button
                          onClick={() => setSelectedModalIndex(idx)}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-black hover:text-slate-600 underline underline-offset-4 cursor-pointer transition-colors"
                        >
                          <span>Læs mere</span>
                          <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-start gap-4 pt-6 border-t border-slate-200 mt-6">
                      {avatar ? (
                        <img
                          src={getAvatarUrl(avatar)}
                          alt={ref.navn}
                          className="w-14 h-14 rounded-full object-cover border border-slate-300 shrink-0 bg-slate-100"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-slate-200 border border-slate-300 shrink-0 flex items-center justify-center font-bold text-black text-base">
                          {ref.navn ? ref.navn.charAt(0) : 'R'}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-black text-base leading-snug">{ref.navn}</h3>
                        <p className="text-xs text-slate-800 font-semibold mt-1">
                          {ref.titel}
                          {ref.virksomhed ? ` · ${ref.virksomhed}` : ''}
                        </p>
                        {ref.relation && (
                          <p className="text-xs text-slate-500 mt-1">
                            {ref.relation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-20">
            <div className="text-center bg-slate-900 text-white p-10 sm:p-14">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                Vil du skabe næste succesfulde rekrutteringsforløb?
              </h3>
              <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto mb-8 leading-relaxed">
                Lad os tage en uforpliktende drøftelse af jeres fremtidige IT- & Tech-behov.
              </p>
              <Link
                to="/kontakt"
                className="inline-block px-8 py-3.5 bg-white text-black font-extrabold transition-colors hover:bg-slate-100"
              >
                Book en samtale med Birgitte
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Grid Modal Viewer */}
      <ReferenceModal
        references={references}
        currentIndex={selectedModalIndex ?? 0}
        isOpen={selectedModalIndex !== null}
        onClose={() => setSelectedModalIndex(null)}
        onSelectIndex={(idx) => setSelectedModalIndex(idx)}
      />
    </>
  )
}
